import React from 'react'
import { SurfacePalette, type } from '../tokens'

interface Props {
  palette: SurfacePalette
  /** Two-digit section number rendered in mono, e.g. "01". */
  number?: string
  /** Short uppercase eyebrow, e.g. "PERFORMANCE". */
  eyebrow?: string
  title: string
  /** Optional word inside the title rendered in italic accent. */
  accentWord?: string
  right?: React.ReactNode
}

const renderTitle = (title: string, accentWord: string | undefined, accent: string) => {
  if (!accentWord) return title
  const idx = title.indexOf(accentWord)
  if (idx < 0) return title
  const before = title.slice(0, idx)
  const after = title.slice(idx + accentWord.length)
  return (
    <>
      {before}
      <em style={{ color: accent, fontStyle: 'italic' }}>{accentWord}</em>
      {after}
    </>
  )
}

export const SectionHead = ({ palette, number, eyebrow, title, accentWord, right }: Props) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 16,
      paddingBottom: 8,
      marginBottom: 14,
      borderBottom: `1px solid ${palette.hairline}`,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
      {(number || eyebrow) && (
        <span
          style={{
            fontFamily: type.family.mono,
            fontSize: type.size.eyebrow,
            fontWeight: type.weight.medium,
            color: palette.textMuted,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}
        >
          {number && <span style={{ color: palette.accent }}>{number}</span>}
          {number && eyebrow && <span style={{ opacity: 0.6 }}>{' / '}</span>}
          {eyebrow}
        </span>
      )}
      <h2
        style={{
          margin: 0,
          fontFamily: type.family.sans,
          fontSize: type.size.h2,
          fontWeight: type.weight.semibold,
          color: palette.text,
          lineHeight: 1.15,
        }}
      >
        {renderTitle(title, accentWord, palette.accent)}
      </h2>
    </div>
    {right && <div style={{ flexShrink: 0 }}>{right}</div>}
  </div>
)
